import React, { useState, useEffect } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    Box,
    Drawer,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Visibility } from '@mui/icons-material';
import { DataGrid, esES, GridCloseIcon } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { getCalls, deleteCall, searchCall } from '../../actions/prospections';
import { SearchProspection } from '../searcher/SearchProspection';
import { ProspectionDetailsModal } from './viewProspecting/ProspectionDetailsModal';
import {
    rowStyle,
    handleConfirmModal,
    handleSuccess,
    handleError,
} from '../../helpers/utils';
import { columnStyleCell } from '../orderTracking/BiasTable';
import { formatDateMX } from '../../helpers/hours';
import { reasonMapping } from '../utils/lists';
import { money } from '../../helpers/utils';
import { SummaryModalProspects } from './summary/SummaryModalProspects';
import { ResultsTable } from './ResultsTable';
import { PendingProspecting } from './PendingProspecting';

const searchIconStyle = { color: '#143f6c', cursor: 'pointer' };

const getReasonLabel = value => {
    const reason = reasonMapping.find(item => item.value === value);
    return reason ? reason.label : value;
};

export const ProspectionsTable = () => {
    const navigate = useNavigate();
    const [showSearcher, setShowSearcher] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedProspectionId, setSelectedProspectionId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [summaryModalOpen, setSummaryModalOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [prospecting, setProspecting] = useState([]);

    // Client tipe mapping
    const typeMapping = {
        NUEVO: 'NEW',
        RECUPERADO: 'REC',
        CAUTIVO: 'CAU',
        CONTACTO: 'PIN',
        PROSPECTO: 'PIR',
        LEAL: 'PIC',
    };

    // Obtaining type of prospecting
    useEffect(() => {
        setIsLoading(true);
        getCalls()
            .then(response => {
                const callsData = response.data.results
                    .map(call => ({
                        id: call.id,
                        name: call.name,
                        phone_number: call.phone_number,
                        email: call.email,
                        sale_value: call.sale_value,
                        created_at: call.created_at,
                        reason_no_purchase: getReasonLabel(call.non_purchase),
                    }))
                    .sort(
                        (a, b) =>
                            new Date(b.created_at) - new Date(a.created_at)
                    );

                setRows(callsData);
                setProspecting(response.data.results);
            })
            .catch(() => {
                handleError('Error al obtener las prospecciones');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // Handling the deletion of a prospecting
    const handleDeleteProspection = id => {
        handleConfirmModal(
            '¡Precaución!',
            '¿Estás seguro de eliminar permanentemente esta prospección?',
            'warning',
            'Sí, eliminar'
        ).then(({ isConfirmed }) => {
            if (isConfirmed) {
                deleteCall(id)
                    .then(() => {
                        setRows(prevRows =>
                            prevRows.filter(row => row.id !== id)
                        );
                        handleSuccess('Prospección eliminada exitosamente');
                    })
                    .catch(error => {
                        handleError('Error eliminando la prospección');
                    });
            }
        });
    };

    const handleOpenSummaryModal = () => {
        setSummaryModalOpen(true);
    };

    const handleCloseSummaryModal = () => {
        setSummaryModalOpen(false);
    };

    const handleViewProspection = prospectionId => {
        setSelectedProspectionId(prospectionId);
        setModalOpen(true);
    };

    const handleOpenDrawer = () => {
        setIsSidebarOpen(true);
    };

    const handleCloseDashboard = () => {
        setIsSidebarOpen(false);
    };

    // Search results
    const generateSearchResults = query => {
        setIsLoading(true);

        const convertedQuery = typeMapping[query?.toUpperCase()] || query;

        if (convertedQuery === null) {
            getCalls()
                .then(response => {
                    const callsData = response.data.results.map(call => ({
                        id: call.id,
                        name: call.name,
                        phone_number: call.phone_number,
                        email: call.email,
                        sale_value: call.sale_value,
                        created_at: call.created_at,
                        reason_no_purchase: getReasonLabel(call.non_purchase),
                    }));
                    setRows(callsData);
                })
                .catch(() => handleError('Error al obtener las prospecciones'))
                .finally(() => setIsLoading(false));
        } else {
            searchCall(convertedQuery)
                .then(response => {
                    const searchResults = response.data.results.map(call => ({
                        id: call.id,
                        name: call.name,
                        phone_number: call.phone_number,
                        email: call.email,
                        sale_value: call.sale_value,
                        created_at: call.created_at,
                        reason_no_purchase: getReasonLabel(call.non_purchase),
                    }));
                    setRows(searchResults);
                })
                .catch(() => handleError('Error al realizar la búsqueda'))
                .finally(() => setIsLoading(false));
        }
    };

    // Table columns
    const columns = [
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 150,
            ...columnStyleCell,
            renderCell: ({ row }) => (
                <>
                    <Tooltip title="Ver prospección">
                        <IconButton
                            style={{ color: '#143f6c' }}
                            aria-label="view"
                            color="primary"
                            onClick={() => handleViewProspection(row.id)}
                        >
                            <Visibility />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar Prospección">
                        <IconButton
                            style={{ color: '#143f6c' }}
                            aria-label="edit"
                            color="primary"
                            onClick={() =>
                                navigate(`/editar-prospeccion/${row.id}`, {
                                    state: { prospectionData: row },
                                })
                            }
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar Prospección">
                        <IconButton
                            style={{ color: '#143f6c' }}
                            aria-label="delete"
                            color="primary"
                            onClick={() => handleDeleteProspection(row.id)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
        { field: 'id', headerName: 'ID', width: 70, ...columnStyleCell },
        {
            field: 'name',
            headerName: 'Nombre del Cliente',
            width: 250,
            ...columnStyleCell,
        },
        {
            field: 'phone_number',
            headerName: 'Teléfono',
            width: 150,
            ...columnStyleCell,
        },
        {
            field: 'email',
            headerName: 'Correo Electrónico',
            width: 250,
            ...columnStyleCell,
        },
        {
            field: 'sale_value',
            headerName: 'Valor de Venta',
            width: 150,
            ...columnStyleCell,
            renderCell: ({ row }) => money(row.sale_value),
        },
        {
            field: 'created_at',
            headerName: 'Fecha de Creación',
            width: 200,
            ...columnStyleCell,
            renderCell: ({ row }) => (
                <Tooltip title={formatDateMX(row.created_at)}>
                    <span className="table-cell-truncate">
                        {formatDateMX(row.created_at)}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'reason_no_purchase',
            headerName: 'Motivo de No Compra',
            width: 250,
            ...columnStyleCell,
            renderCell: ({ row }) => (
                <Tooltip title={row.reason_no_purchase}>
                    <span className="table-cell-truncate">
                        {row.reason_no_purchase}
                    </span>
                </Tooltip>
            ),
        },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Stack
                direction="row"
                alignItems="flex-end"
                justifyContent="space-between"
                style={{
                    border: '1px solid white',
                    padding: '20px 20px 0 0',
                    marginBottom: '40px',
                    width: '100%',
                    ...columnStyleCell,
                }}
            >
                <Typography variant="h4" gutterBottom>
                    Prospecciones
                </Typography>
                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{ marginRight: '10px' }}
                        onClick={handleOpenDrawer}
                    >
                        Análisis de Prospecciones
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        style={{ marginRight: '10px' }}
                        onClick={handleOpenSummaryModal}
                    >
                        Generar Reporte
                    </Button>

                    <Button
                        variant="contained"
                        style={{ backgroundColor: '#143f6c' }}
                        onClick={() => navigate('/nueva-prospeccion')}
                    >
                        Nueva Prospección
                    </Button>
                </Box>
            </Stack>

            <hr />

            <Typography
                variant="h6"
                gutterBottom
                style={{
                    ...rowStyle,
                    display: 'flex',
                    alignItems: 'center',
                    margin: '20px 0 10px 0',
                }}
            >
                Búsqueda global{' '}
                <div
                    onClick={() => setShowSearcher(!showSearcher)}
                    style={{ marginLeft: '10px' }}
                >
                    {showSearcher ? (
                        <SearchOffIcon style={searchIconStyle} />
                    ) : (
                        <SearchIcon style={searchIconStyle} />
                    )}
                </div>
            </Typography>

            {showSearcher && (
                <SearchProspection
                    generateRows={generateSearchResults}
                    handleClose={() => setShowSearcher(false)}
                    rowsState={{ data: rows }}
                    setRowsState={setRows}
                />
            )}

            {isLoading ? (
                <center style={{ padding: '20%' }}>
                    <CircularProgress />
                </center>
            ) : rows.length > 0 ? (
                <DataGrid
                    rows={rows}
                    autoHeight
                    columns={columns}
                    localeText={
                        esES.components.MuiDataGrid.defaultProps.localeText
                    }
                    sx={{
                        '& .header-blue': {
                            backgroundColor: '#e0f3ff',
                            color: '#143f6c',
                        },
                    }}
                />
            ) : (
                <Alert severity="info">No hay ninguna prospección</Alert>
            )}

            <ProspectionDetailsModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                prospectionId={selectedProspectionId}
            />

            <SummaryModalProspects
                open={summaryModalOpen}
                onClose={handleCloseSummaryModal}
            />

            <Drawer
                anchor="right"
                open={isSidebarOpen}
                onClose={handleCloseDashboard}
                sx={{ width: '80%', minHeight: '100vh' }}
            >
                <Box
                    sx={{
                        width: '80vw',
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: '#143F6C',
                            padding: '10px',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            position: 'sticky',
                            width: '100%',
                            top: '0',
                            zIndex: '10000',
                        }}
                    >
                        <Typography variant="h6" sx={{ marginLeft: '15px' }}>
                            Análisis de prospecciones
                        </Typography>
                        <IconButton
                            onClick={handleCloseDashboard}
                            sx={{ color: '#fff' }}
                        >
                            <GridCloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ padding: 2, flexGrow: 1 }}>
                        <PendingProspecting />
                        <ResultsTable prospecting={prospecting} />
                    </Box>
                </Box>
            </Drawer>
        </div>
    );
};
